import React, { useState } from "react";
import {
  Drawer,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  IconButton,
  useTheme,
  Typography,
  Collapse,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HomeIcon from "@mui/icons-material/Home";
import ExploreIcon from "@mui/icons-material/Explore";
import LibraryBooksOutlinedIcon from "@mui/icons-material/LibraryBooksOutlined";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ContactMailOutlinedIcon from "@mui/icons-material/ContactMailOutlined";
import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import { IconGradient, SmallTextGradient } from "../Gradient";
import { Button } from "../Buttons";
import { Modal } from "../Dialogs";
import { STORY_CATEGORIES } from "../StoryForm/StoryFormConstants";
import { formatCategoryName } from "../../utility/category.utils";
import { getGlassCardStyles } from "../../utility/glassCard.utils";
import { useAdminRole } from "../../hooks/useAdminRole";
import type { StoryCategory } from "../../types/models";
import type { User } from "firebase/auth";

type UserProfile = { displayName?: string | null };
type Profile = { photoURL?: string | null } | null | undefined;

type MobileDrawerProps = {
  open: boolean;
  onClose: () => void;
  userId?: string;
  user: UserProfile;
  profile: Profile;
  active: string | null;
  setActive: (key: string) => void;
  handleLogout: () => void;
  authUser?: User | null;
};

const MobileDrawer: React.FC<MobileDrawerProps> = ({
  open,
  onClose,
  userId,
  user,
  profile,
  active,
  setActive,
  handleLogout,
  authUser,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [exploreMenuOpen, setExploreMenuOpen] = useState(false);
  const [writeMenuOpen, setWriteMenuOpen] = useState(false);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const [challengesMenuOpen, setChallengesMenuOpen] = useState(false);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  
  const { isModerator } = useAdminRole(authUser || null);

  const handleDrawerClose = () => {
    setExploreMenuOpen(false);
    setWriteMenuOpen(false);
    setMoreMenuOpen(false);
    setChallengesMenuOpen(false);
    onClose();
  };

  const handleCategoryClick = (category: StoryCategory) => {
    handleDrawerClose();
    setActive("explore");
    navigate(`/explore?category=${category}`);
  };

  // Category groups for drawer
  const CATEGORY_GROUPS = {
    Fiction: [
      "FanFiction",
      "ScienceFiction",
      "Fantasy",
      "Romance",
      "Mystery",
      "Thriller",
      "Horror",
      "Historical",
      "Action",
      "Adventure",
      "Comedy",
      "Drama",
      "ChickLit",
      "YoungAdult",
      "ShortStory",
      "GeneralFiction",
    ],
    "Non-Fiction": [
      "PersonalGrowth",
      "MentalHealth",
      "Career",
      "Education",
      "HealthAndFitness",
      "Lifestyle",
      "Finance",
      "Food",
      "Travel",
      "Tech",
      "Religion",
      "Culture",
      "BooksAndReviews",
      "CollegeLife",
      "NonFiction",
    ],
    Other: [
      "Poetry",
      "Paranormal",
      "Urban",
      "Western",
      "Other",
    ],
  };

  const handleLogoutClick = () => {
    setLogoutDialogOpen(true);
  };

  const handleConfirmLogout = () => {
    setLogoutDialogOpen(false);
    handleLogout();
    onClose();
  };

  const handleCancelLogout = () => {
    setLogoutDialogOpen(false);
  };

  const handleNavClick = (path: string, key: string) => {
    navigate(path);
    setActive(key);
    handleDrawerClose();
  };

  return (
    <>
      <Drawer
        anchor="left"
        open={open}
        onClose={handleDrawerClose}
        PaperProps={{
          sx: (theme) => ({
            ...getGlassCardStyles(theme),
            width: { xs: "85vw", sm: 320 },
            maxWidth: 400,
            borderLeft: "none",
          }),
        }}
      >
        <Box sx={{ p: 2, position: "relative", zIndex: 1 }}>
          {/* Header with Close Button */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            {userId ? (
              <Typography variant="h6">
                <SmallTextGradient gradientVariant="default" sx={{ fontWeight: 700, fontSize: "1.25rem" }}>
                  {user.displayName || "User"}
                </SmallTextGradient>
              </Typography>
            ) : (
              <Typography variant="h6" component={Link} to="/" onClick={() => handleNavClick("/", "home")}>
                <SmallTextGradient gradientVariant="default" sx={{ fontWeight: 700, fontSize: "1.25rem" }}>
                  Your Story
                </SmallTextGradient>
              </Typography>
            )}
            <IconButton onClick={handleDrawerClose} sx={{ color: "text.secondary" }}>
              <CloseIcon />
            </IconButton>
          </Box>


          {/* Navigation List */}
          <List sx={{ width: "100%", p: 0 }}>
            {/* Home */}
            <ListItem disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                component={Link}
                to="/"
                onClick={() => handleNavClick("/", "home")}
                selected={active === "home"}
                sx={(theme) => ({
                  borderRadius: 1,
                  "&.Mui-selected": {
                    backgroundColor: theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.05)"
                      : "rgba(0,0,0,0.04)",
                  },
                  "&:hover": {
                    backgroundColor: theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.05)"
                      : "rgba(0,0,0,0.04)",
                  },
                })}
              >
                <IconGradient gradientVariant="default">
                  <HomeIcon fontSize="small" />
                </IconGradient>
                <ListItemText
                  primary={
                    <SmallTextGradient variant="body2" gradientVariant="default" sx={{ fontWeight: 600, ml: 1.5 }}>
                      Home
                    </SmallTextGradient>
                  }
                />
              </ListItemButton>
            </ListItem>

            {/* Explore All */}
            <ListItem disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                component={Link}
                to="/explore"
                onClick={() => handleNavClick("/explore", "explore")}
                selected={active === "explore"}
                sx={(theme) => ({
                  borderRadius: 1,
                  "&.Mui-selected": {
                    backgroundColor: theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.05)"
                      : "rgba(0,0,0,0.04)",
                  },
                  "&:hover": {
                    backgroundColor: theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.05)"
                      : "rgba(0,0,0,0.04)",
                  },
                })}
              >
                <IconGradient gradientVariant="default">
                  <ExploreIcon fontSize="small" />
                </IconGradient>
                <ListItemText
                  primary={
                    <SmallTextGradient variant="body2" gradientVariant="default" sx={{ fontWeight: 600, ml: 1.5 }}>
                      Explore All
                    </SmallTextGradient>
                  }
                />
              </ListItemButton>
            </ListItem>

            {/* Explore Categories */}
            <ListItem disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                onClick={() => setExploreMenuOpen(!exploreMenuOpen)}
                sx={(theme) => ({
                  borderRadius: 1,
                  "&:hover": {
                    backgroundColor: theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.05)"
                      : "rgba(0,0,0,0.04)",
                  },
                })}
              >
                <IconGradient gradientVariant="default">
                  <ExploreIcon fontSize="small" />
                </IconGradient>
                <ListItemText
                  primary={
                    <SmallTextGradient variant="body2" gradientVariant="default" sx={{ fontWeight: 600, ml: 1.5 }}>
                      Categories
                    </SmallTextGradient>
                  }
                />
                {exploreMenuOpen ? (
                  <ExpandMoreIcon sx={{ ml: "auto" }} />
                ) : (
                  <ChevronRightIcon sx={{ ml: "auto" }} />
                )}
              </ListItemButton>
            </ListItem>

            {/* Explore Categories Submenu */}
            <Collapse in={exploreMenuOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {Object.entries(CATEGORY_GROUPS).map(([groupName, categories]) => (
                  <Box key={groupName} sx={{ px: 2, py: 1 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        textTransform: "uppercase",
                        letterSpacing: 1,
                        fontSize: "0.7rem",
                        fontWeight: 700,
                        color: "text.secondary",
                        opacity: 0.7,
                        mb: 0.5,
                        display: "block",
                      }}
                    >
                      {groupName}
                    </Typography>
                    {categories
                      .filter((cat) => STORY_CATEGORIES.includes(cat as StoryCategory))
                      .map((category) => (
                        <ListItem disablePadding key={category} sx={{ mb: 0.25 }}>
                          <ListItemButton
                            onClick={() => handleCategoryClick(category as StoryCategory)}
                            sx={(theme) => ({
                              borderRadius: 1,
                              pl: 3,
                              py: 0.75,
                              "&:hover": {
                                backgroundColor: theme.palette.mode === "dark"
                                  ? "rgba(255,255,255,0.05)"
                                  : "rgba(0,0,0,0.04)",
                              },
                            })}
                          >
                            <ListItemText
                              primary={
                                <SmallTextGradient
                                  variant="body2"
                                  gradientVariant="subtle"
                                  sx={{ fontWeight: 400, fontSize: "0.8125rem" }}
                                >
                                  {formatCategoryName(category as StoryCategory)}
                                </SmallTextGradient>
                              }
                            />
                          </ListItemButton>
                        </ListItem>
                      ))}
                  </Box>
                ))}
              </List>
            </Collapse>

            {/* Challenges Menu */}
            <ListItem disablePadding sx={{ mb: 0.5, mt: 1 }}>
              <ListItemButton
                onClick={() => setChallengesMenuOpen(!challengesMenuOpen)}
                sx={(theme) => ({
                  borderRadius: 1,
                  "&:hover": {
                    backgroundColor: theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.05)"
                      : "rgba(0,0,0,0.04)",
                  },
                })}
              >
                <IconGradient gradientVariant="default">
                  <EmojiEventsIcon fontSize="small" />
                </IconGradient>
                <ListItemText
                  primary={
                    <SmallTextGradient variant="body2" gradientVariant="default" sx={{ fontWeight: 600, ml: 1.5 }}>
                      Challenges
                    </SmallTextGradient>
                  }
                />
                {challengesMenuOpen ? (
                  <ExpandMoreIcon sx={{ ml: "auto" }} />
                ) : (
                  <ChevronRightIcon sx={{ ml: "auto" }} />
                )}
              </ListItemButton>
            </ListItem>

            {/* Challenges Submenu */}
            <Collapse in={challengesMenuOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                <ListItem disablePadding sx={{ mb: 0.25 }}>
                  <ListItemButton
                    component={Link}
                    to="/challenges/ink-masters"
                    onClick={() => handleNavClick("/challenges/ink-masters", "ink-masters")}
                    sx={(theme) => ({
                      borderRadius: 1,
                      pl: 3,
                      py: 0.75,
                      "&:hover": {
                        backgroundColor: theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.05)"
                          : "rgba(0,0,0,0.04)",
                      },
                    })}
                  >
                    <ListItemText
                      primary={
                        <SmallTextGradient
                          variant="body2"
                          gradientVariant="subtle"
                          sx={{ fontWeight: 400, fontSize: "0.8125rem" }}
                        >
                          Ink Masters
                        </SmallTextGradient>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              </List>
            </Collapse>

            {/* Write Menu */}
            {userId && (
              <>
                <ListItem disablePadding sx={{ mb: 0.5, mt: 1 }}>
                  <ListItemButton
                    onClick={() => setWriteMenuOpen(!writeMenuOpen)}
                    selected={active === "create" || active === "create-story"}
                    sx={(theme) => ({
                      borderRadius: 1,
                      "&.Mui-selected": {
                        backgroundColor: theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.05)"
                          : "rgba(0,0,0,0.04)",
                      },
                      "&:hover": {
                        backgroundColor: theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.05)"
                          : "rgba(0,0,0,0.04)",
                      },
                    })}
                  >
                    <IconGradient gradientVariant="default">
                      <EditNoteOutlinedIcon fontSize="small" />
                    </IconGradient>
                    <ListItemText
                      primary={
                        <SmallTextGradient variant="body2" gradientVariant="default" sx={{ fontWeight: 600, ml: 1.5 }}>
                          Write
                        </SmallTextGradient>
                      }
                    />
                    {writeMenuOpen ? (
                      <ExpandMoreIcon sx={{ ml: "auto" }} />
                    ) : (
                      <ChevronRightIcon sx={{ ml: "auto" }} />
                    )}
                  </ListItemButton>
                </ListItem>

                {/* Write Submenu */}
                <Collapse in={writeMenuOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    <ListItem disablePadding sx={{ pl: 4, mb: 0.5 }}>
                      <ListItemButton
                        component={Link}
                        to="/create-story"
                        onClick={() => handleNavClick("/create-story", "create-story")}
                        sx={(theme) => ({
                          borderRadius: 1,
                          "&:hover": {
                            backgroundColor: theme.palette.mode === "dark"
                              ? "rgba(255,255,255,0.05)"
                              : "rgba(0,0,0,0.04)",
                          },
                        })}
                      >
                        <IconGradient gradientVariant="subtle">
                          <EditNoteOutlinedIcon fontSize="small" />
                        </IconGradient>
                        <ListItemText
                          primary={
                            <SmallTextGradient variant="body2" gradientVariant="subtle" sx={{ fontWeight: 500, ml: 1.5 }}>
                              Create Story
                            </SmallTextGradient>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                    <Box sx={{ px: 4, py: 0.5 }}>
                      <Divider
                        sx={{
                          borderColor: (theme) =>
                            theme.palette.mode === "dark"
                              ? "rgba(255,255,255,0.08)"
                              : "rgba(0,0,0,0.08)",
                        }}
                      />
                    </Box>
                    <ListItem disablePadding sx={{ pl: 4, mb: 0.5 }}>
                      <ListItemButton
                        component={Link}
                        to="/my-stories?status=draft"
                        onClick={() => handleNavClick("/my-stories?status=draft", "mystories")}
                        sx={(theme) => ({
                          borderRadius: 1,
                          "&:hover": {
                            backgroundColor: theme.palette.mode === "dark"
                              ? "rgba(255,255,255,0.05)"
                              : "rgba(0,0,0,0.04)",
                          },
                        })}
                      >
                        <IconGradient gradientVariant="subtle">
                          <DescriptionOutlinedIcon fontSize="small" />
                        </IconGradient>
                        <ListItemText
                          primary={
                            <SmallTextGradient variant="body2" gradientVariant="subtle" sx={{ fontWeight: 500, ml: 1.5 }}>
                              Drafts
                            </SmallTextGradient>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                  </List>
                </Collapse>
              </>
            )}

            {/* More Menu */}
            <ListItem disablePadding sx={{ mb: 0.5, mt: 1 }}>
              <ListItemButton
                onClick={() => setMoreMenuOpen(!moreMenuOpen)}
                sx={(theme) => ({
                  borderRadius: 1,
                  "&:hover": {
                    backgroundColor: theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.05)"
                      : "rgba(0,0,0,0.04)",
                  },
                })}
              >
                <IconGradient gradientVariant="default">
                  <InfoOutlinedIcon fontSize="small" />
                </IconGradient>
                <ListItemText
                  primary={
                    <SmallTextGradient variant="body2" gradientVariant="default" sx={{ fontWeight: 600, ml: 1.5 }}>
                      More
                    </SmallTextGradient>
                  }
                />
                {moreMenuOpen ? (
                  <ExpandMoreIcon sx={{ ml: "auto" }} />
                ) : (
                  <ChevronRightIcon sx={{ ml: "auto" }} />
                )}
              </ListItemButton>
            </ListItem>

            {/* More Submenu */}
            <Collapse in={moreMenuOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                <ListItem disablePadding sx={{ pl: 4, mb: 0.25 }}>
                  <ListItemButton
                    component={Link}
                    to="/about"
                    onClick={() => handleNavClick("/about", "about")}
                    sx={(theme) => ({
                      borderRadius: 1,
                      "&:hover": {
                        backgroundColor: theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.05)"
                          : "rgba(0,0,0,0.04)",
                      },
                    })}
                  >
                    <IconGradient gradientVariant="subtle">
                      <InfoOutlinedIcon fontSize="small" />
                    </IconGradient>
                    <ListItemText
                      primary={
                        <SmallTextGradient variant="body2" gradientVariant="subtle" sx={{ fontWeight: 500, ml: 1.5 }}>
                          About
                        </SmallTextGradient>
                      }
                    />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding sx={{ pl: 4, mb: 0.25 }}>
                  <ListItemButton
                    component={Link}
                    to="/contact"
                    onClick={() => handleNavClick("/contact", "contact")}
                    sx={(theme) => ({
                      borderRadius: 1,
                      "&:hover": {
                        backgroundColor: theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.05)"
                          : "rgba(0,0,0,0.04)",
                      },
                    })}
                  >
                    <IconGradient gradientVariant="subtle">
                      <ContactMailOutlinedIcon fontSize="small" />
                    </IconGradient>
                    <ListItemText
                      primary={
                        <SmallTextGradient variant="body2" gradientVariant="subtle" sx={{ fontWeight: 500, ml: 1.5 }}>
                          Contact
                        </SmallTextGradient>
                      }
                    />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding sx={{ pl: 4, mb: 0.5 }}>
                  <ListItemButton
                    component={Link}
                    to="/careers"
                    onClick={() => handleNavClick("/careers", "careers")}
                    sx={(theme) => ({
                      borderRadius: 1,
                      "&:hover": {
                        backgroundColor: theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.05)"
                          : "rgba(0,0,0,0.04)",
                      },
                    })}
                  >
                    <IconGradient gradientVariant="subtle">
                      <WorkOutlineOutlinedIcon fontSize="small" />
                    </IconGradient>
                    <ListItemText
                      primary={
                        <SmallTextGradient variant="body2" gradientVariant="subtle" sx={{ fontWeight: 500, ml: 1.5 }}>
                          Careers
                        </SmallTextGradient>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              </List>
            </Collapse>
          </List>

          {/* User Section */}
          <Divider sx={{ my: 2 }} />
          {userId ? (
            <>
              {/* Dashboard */}
              <ListItem disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  component={Link}
                  to="/dashboard"
                  onClick={() => handleNavClick("/dashboard", "dashboard")}
                  selected={active === "dashboard"}
                  sx={(theme) => ({
                    borderRadius: 1,
                    "&.Mui-selected": {
                      backgroundColor: theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.05)"
                        : "rgba(0,0,0,0.04)",
                    },
                    "&:hover": {
                      backgroundColor: theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.05)"
                        : "rgba(0,0,0,0.04)",
                    },
                  })}
                >
                  <IconGradient gradientVariant="default">
                    <DashboardOutlinedIcon fontSize="small" />
                  </IconGradient>
                  <ListItemText
                    primary={
                      <SmallTextGradient variant="body2" gradientVariant="default" sx={{ fontWeight: 600, ml: 1.5 }}>
                        Dashboard
                      </SmallTextGradient>
                    }
                  />
                </ListItemButton>
              </ListItem>

              {/* My Stories */}
              <ListItem disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  component={Link}
                  to="/my-stories"
                  onClick={() => handleNavClick("/my-stories", "mystories")}
                  selected={active === "mystories"}
                  sx={(theme) => ({
                    borderRadius: 1,
                    "&.Mui-selected": {
                      backgroundColor: theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.05)"
                        : "rgba(0,0,0,0.04)",
                    },
                    "&:hover": {
                      backgroundColor: theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.05)"
                        : "rgba(0,0,0,0.04)",
                    },
                  })}
                >
                  <IconGradient gradientVariant="default">
                    <LibraryBooksOutlinedIcon fontSize="small" />
                  </IconGradient>
                  <ListItemText
                    primary={
                      <SmallTextGradient variant="body2" gradientVariant="default" sx={{ fontWeight: 600, ml: 1.5 }}>
                        My Stories
                      </SmallTextGradient>
                    }
                  />
                </ListItemButton>
              </ListItem>

              {/* My Bookmarks */}
              <ListItem disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  component={Link}
                  to="/my-bookmarks"
                  onClick={() => handleNavClick("/my-bookmarks", "mybookmarks")}
                  selected={active === "mybookmarks"}
                  sx={(theme) => ({
                    borderRadius: 1,
                    "&.Mui-selected": {
                      backgroundColor: theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.05)"
                        : "rgba(0,0,0,0.04)",
                    },
                    "&:hover": {
                      backgroundColor: theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.05)"
                        : "rgba(0,0,0,0.04)",
                    },
                  })}
                >
                  <IconGradient gradientVariant="default">
                    <BookmarkIcon fontSize="small" />
                  </IconGradient>
                  <ListItemText
                    primary={
                      <SmallTextGradient variant="body2" gradientVariant="default" sx={{ fontWeight: 600, ml: 1.5 }}>
                        My Bookmarks
                      </SmallTextGradient>
                    }
                  />
                </ListItemButton>
              </ListItem>

              {/* Moderation Queue (Admin/Moderator only) */}
              {isModerator && (
                <ListItem disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    component={Link}
                    to="/moderation"
                    onClick={() => handleNavClick("/moderation", "moderation")}
                    selected={active === "moderation"}
                    sx={(theme) => ({
                      borderRadius: 1,
                      "&.Mui-selected": {
                        backgroundColor: theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.05)"
                          : "rgba(0,0,0,0.04)",
                      },
                      "&:hover": {
                        backgroundColor: theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.05)"
                          : "rgba(0,0,0,0.04)",
                      },
                    })}
                  >
                    <IconGradient gradientVariant="default">
                      <AdminPanelSettingsIcon fontSize="small" />
                    </IconGradient>
                    <ListItemText
                      primary={
                        <SmallTextGradient variant="body2" gradientVariant="default" sx={{ fontWeight: 600, ml: 1.5 }}>
                          Moderation Queue
                        </SmallTextGradient>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              )}

              {/* Logout */}
              <ListItem disablePadding>
                <ListItemButton
                  onClick={handleLogoutClick}
                  sx={(theme) => ({
                    borderRadius: 1,
                    color: theme.palette.error.main,
                    "&:hover": {
                      backgroundColor: theme.palette.mode === "dark"
                        ? "rgba(211,47,47,0.15)"
                        : "rgba(211,47,47,0.1)",
                    },
                  })}
                >
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      "& svg": {
                        fontSize: 18,
                        color: theme.palette.error.main,
                      },
                    }}
                  >
                    <LogoutOutlinedIcon fontSize="small" />
                  </Box>
                  <ListItemText
                    primary={
                      <SmallTextGradient
                        variant="body2"
                        gradientVariant="default"
                        sx={{
                          fontWeight: 600,
                          ml: 1.5,
                          color: theme.palette.error.main,
                        }}
                      >
                        Logout
                      </SmallTextGradient>
                    }
                  />
                </ListItemButton>
              </ListItem>
            </>
          ) : (
            <Box sx={{ px: 2 }}>
              <Button
                variant="outlined"
                component={Link}
                to="/auth"
                onClick={() => {
                  handleNavClick("/auth", "login");
                }}
                fullWidth
              >
                Login
              </Button>
            </Box>
          )}
        </Box>
      </Drawer>

      {/* Logout Confirmation Dialog */}
      <Modal
        open={logoutDialogOpen}
        onClose={handleCancelLogout}
        onCancel={handleCancelLogout}
        onConfirm={handleConfirmLogout}
        title="Ready to sign out?"
        description="We'll miss you! Sign in again whenever you're ready to continue your storytelling journey."
        icon={<WarningAmberRoundedIcon />}
        iconColor={theme.palette.error.main}
        cancelText="Cancel"
        confirmText="Logout"
        confirmButtonColor="error"
      />
    </>
  );
};

export default MobileDrawer;

